import Station, { StationAPIResponse, StationStatus } from "@/classes/station";
import { DATA_API_SERVICE } from "@/appConfig";
import APIOrganization from "@/api/auth/organization";
import Organization from "@/classes/organization";
import Property from "@/classes/property";
import StationDisconnect, { StationDisconnectResponse } from "@/classes/stationDisconnect";
import sortBy from "lodash/sortBy";
import API from "@/api/api";
import subDays from "date-fns/subDays";
import endOfDay from "date-fns/endOfDay";
import StationDisconnectHistoryItem, {
  StationDisconnectHistoryItemResponse
} from "@/classes/stationDisconnectHistoryItem";
import StationConfig, { StationConfigAPIResponse } from "@/classes/stationConfig";
import StationOCPPLogsItem, { StationOCPPLogsItemResponse } from "@/classes/stationOCPPLogsItem";
import APICreditProgram from "@/api/auth/creditProgram";
import { EntityUptimeAPIResponse } from "@/classes/entityUptime";
import apolloClient from "@/api/charge";
import { gql } from "@apollo/client/core";

async function getUnhealthyStations(): Promise<StationDisconnectResponse[]> {
  const res = await apolloClient.query({
    query: gql`
      query UnhealthyStations {
        unhealthyStations {
          serial
          lastCommunication
          propertyId
          organizationId
          chargieId: barcode
          disconnect
          productionStatus
          status
        }
      }
    `
  });
  return res.data?.unhealthyStations.map(row => {
    return new StationDisconnect({
      ...row
    });
  });
}

async function mapStationsToDisconnectStatus(stations: Station[]) {
  const [stationDisconnects, stationStatuses] = await Promise.all([findDisconnected(), getUnhealthyStations()]);
  const stationUnhealthySerialNumbers: string[] = [];
  const stationDisconnectsSerialNumbers = stationDisconnects.map(stationDisconnect => {
    if (stationDisconnect.disconnect) return stationDisconnect.serial;
    else stationUnhealthySerialNumbers.push(stationDisconnect.serial);
  });
  stations.forEach(station => {
    if (stationDisconnectsSerialNumbers.includes(station.serialNo)) {
      station._isDisconnected = true;
    } else if (stationUnhealthySerialNumbers.includes(station.serialNo)) {
      station._isUnhealthy = true;
    }

    const status = stationStatuses.find(s => s.serial === station.serialNo)?.status as StationStatus;
    if (status) {
      station.status = status;
    }
  });
  return stations;
}

async function getStationById(stationId: string, isAdmin: boolean): Promise<Station> {
  const res = await apolloClient.query({
    query: gql`
      query Station($filter: StationFilter) {
        station(filter: $filter) {
          clusterId
          connector: connectors
          createdTime: createdAt
          exclusive: isExclusive
          exclusiveUsr1: exclusiveUser1
          exclusiveUsr2: exclusiveUser2
          firmwareVer: firmwareVersion
          floor
          fundingType
          id
          installed: installedAt
          isFree
          isGateway
          isSubscription
          itemStatus
          macAddress
          make
          model
          modifiedBy
          modifiedTime: modifiedAt
          note
          organizationId
          parkSpace: parkingSpace
          productionStatus
          propertyId
          qrCode
          registeredTime: registeredAt
          serialNo: serialNumber
          stationLevel
          status: ocppStatus
          warrantyDate
          # TODO: Uncomment when backend is pushed
          latitude
          longitude
        }
      }
    `,
    variables: {
      filter: {
        id: stationId
      }
    }
  });
  const stationCreditPrograms = await APICreditProgram.getStationCreditPrograms(stationId, isAdmin);
  const station = Station.fromAPI(res.data?.station, undefined, undefined, isAdmin ? stationCreditPrograms : undefined);
  const mappedStations = await mapStationsToDisconnectStatus([station]);
  return mappedStations[0];
}

async function getStationBySerial(serial: string, isAdmin: boolean): Promise<Station> {
  const stationResponse = await API.get<StationAPIResponse>(`${DATA_API_SERVICE}/v2/station/serial/${serial}`);
  const stationCreditPrograms = await APICreditProgram.getStationCreditPrograms(stationResponse.data.id, isAdmin);
  const station = Station.fromAPI(
    stationResponse.data,
    undefined,
    undefined,
    isAdmin ? stationCreditPrograms : undefined
  );
  const mappedStations = await mapStationsToDisconnectStatus([station]);
  return mappedStations[0];
}

async function findAll(): Promise<Station[]> {
  const res = await apolloClient.query({
    query: gql`
      query Stations {
        stations {
          clusterId
          connector: connectors
          createdTime: createdAt
          exclusive: isExclusive
          exclusiveUsr1: exclusiveUser1
          exclusiveUsr2: exclusiveUser2
          firmwareVer: firmwareVersion
          floor
          fundingType
          id
          installed: installedAt
          isFree
          isGateway
          isSubscription
          itemStatus
          macAddress
          make
          model
          modifiedBy
          modifiedTime: modifiedAt
          note
          organizationId
          parkSpace: parkingSpace
          productionStatus
          propertyId
          qrCode
          registeredTime: registeredAt
          serialNo: serialNumber
          stationLevel
          # status: ocppStatus
          warrantyDate
          latitude
          longitude
        }
      }
    `
  });
  const stations = res.data?.stations.map(stationResponse => Station.fromAPI(stationResponse));
  return await mapStationsToDisconnectStatus(stations);
}

async function getStationsByPropertyId(propertyId: string) {
  const organizations = await APIOrganization.findAllWithFullTree();
  let organizationFound: Organization | undefined;
  let propertyFound: Property | undefined;
  for (const organization of organizations) {
    const propertyMatch = organization.properties.find(property => property.id === propertyId);
    if (propertyMatch) {
      organizationFound = organization;
      propertyFound = propertyMatch;
      break;
    }
  }
  if (!propertyFound) throw new Error(`Property not found: ${propertyId}`);
  return {
    organization: organizationFound!,
    property: propertyFound,
    stations: propertyFound.stations
  };
}

async function getUnregisteredStations() {
  const response = await API.get<StationAPIResponse[]>(`${DATA_API_SERVICE}/v2/station/unregistered`, {
    cache: {
      ignoreCache: true
    }
  });
  return response.data.map(stationResponse => Station.fromAPI(stationResponse));
}

async function remoteStationControl(query: {
  entityId: string;
  entityEnum: "CLUSTER" | "ORGANIZATION" | "PANEL" | "PROPERTY" | "STATION" | "USER";
  action:
    | "FIRMWARE_UPDATE"
    | "GET_CONFIG"
    | "REMOTE_START"
    | "REMOTE_STOP"
    | "RESET_HARD"
    | "RESET_SOFT"
    | "UPDATE_CONFIG";
  key?: string; // only used for UPDATE_CONFIG
  value?: string; // only used for UPDATE_CONFIG
  connector?: number; // used for Start and Stop
}) {
  await API.post(`${DATA_API_SERVICE}/remote`, query);
}

async function startStation(stationId: Station["id"], connectorNumber) {
  await remoteStationControl({
    entityId: stationId,
    entityEnum: "STATION",
    action: "REMOTE_START",
    connector: connectorNumber
  });
}

async function stopStation(stationId: Station["id"], connectorNumber) {
  await remoteStationControl({
    entityId: stationId,
    entityEnum: "STATION",
    action: "REMOTE_STOP",
    connector: connectorNumber
  });
}

async function createStation(userStation: Station) {
  const station = new Station(userStation);
  const response = await API.post<{ id: string }>(`${DATA_API_SERVICE}/v2/station`, {
    ...station,
    installed: undefined
  });
  return response.data.id;
}

async function deleteStation(stationId: Station["id"]) {
  return await API.delete(`${DATA_API_SERVICE}/v2/station/delete/${stationId}`);
}

async function updateStation(userStation: Station) {
  const station = new Station(userStation);
  const promises = [
    API.put(`${DATA_API_SERVICE}/v2/station/${userStation.id}`, {
      ...station,
      installed: undefined
    })
  ] as Promise<any>[];
  if (userStation.hasChangedCreditPrograms) {
    promises.push(APICreditProgram.createStationCreditPrograms(userStation.creditPrograms));
  }
  return Promise.all(promises);
}

async function findDisconnectedWithMappedOrg() {
  const [stationDisconnectedResponse, organizations] = await Promise.all([
    // TODO: Put this back in when fixed
    // getUnhealthyStations(),
    API.get<StationDisconnectResponse[]>(`${DATA_API_SERVICE}/v2/station/unhealthy`),
    APIOrganization.findAllWithFullTree()
  ]);
  const properties = organizations
    .map(organization => {
      return organization.properties;
    })
    .flat();

  // Remove the properties from the organization for faster search
  organizations.forEach(organization => {
    organization.properties = [];
    organization.stations = [];
  });
  properties.forEach(property => {
    property.stations = [];
  });

  const stationsDisconnected = stationDisconnectedResponse.data.map(row => {
    return new StationDisconnect({
      ...row,
      property: properties.find(property => property.id === row.propertyId)!,
      organization: organizations.find(organization => organization.id === row.organizationId)!
    });
  });
  return sortBy(stationsDisconnected, station => {
    return station.lastCommunication ? station.lastCommunication.getTime() : 0;
  }).reverse();
}

async function findDisconnected() {
  try {
    // TODO: Put this back in when fixed
    // const stationsDisconnected = await getUnhealthyStations();
    // return sortBy(
    //   stationsDisconnected.map(s => new StationDisconnect(s)),
    //   station => {
    //     return station.lastCommunication ? station.lastCommunication.getTime() : 0;
    //   }
    // ).reverse();
    const response = await API.get<StationDisconnectResponse[]>(`${DATA_API_SERVICE}/v2/station/unhealthy`);
    const stationsDisconnected = response.data.map(row => {
      return new StationDisconnect({
        ...row
      });
    });
    return sortBy(stationsDisconnected, station => {
      return station.lastCommunication ? station.lastCommunication.getTime() : 0;
    }).reverse();
  } catch (e) {
    return [];
  }
}

async function getOCPPLogs(
  query?: Partial<{
    stationId: string;
    start: Date;
    end: Date;
  }>
) {
  let startDate = subDays(new Date(), 7);
  let endDate = endOfDay(new Date());
  if (query) {
    if (query.start) startDate = query.start;
    if (query.end) endDate = query.end;
  }
  const response = await API.post<StationOCPPLogsItemResponse[]>(`${DATA_API_SERVICE}/v2/station/logs`, {
    ...query,
    start: startDate,
    end: endDate
  });
  const items = response.data.map(item => StationOCPPLogsItem.fromAPI(item));
  return sortBy(items, item => item.createdAt.getTime()).reverse();
}

async function getStationFirmware() {
  const response = await API.get(`${DATA_API_SERVICE}/v2/station/firmware`);
  return response.data;
}

async function getDisconnectedHistory(
  query?: Partial<{
    organizationId: string;
    propertyId: string;
    stationId: string;
    serial: string;
    chargieId: string;
    start: Date;
    end: Date;
  }>
) {
  let startDate = subDays(new Date(), 7);
  let endDate = endOfDay(new Date());
  if (query) {
    if (query.start) startDate = query.start;
    if (query.end) endDate = query.end;
  }
  const response = await API.post<StationDisconnectHistoryItemResponse[]>(
    `${DATA_API_SERVICE}/v2/station/disconnected/history`,
    {
      ...query,
      start: startDate,
      end: endDate
    }
  );
  const historyItems = response.data.map(item => StationDisconnectHistoryItem.fromAPI(item));
  return sortBy(historyItems, item => item.createdAt.getTime()).reverse();
}

async function registerStation(station: Station) {
  return await API.put(`${DATA_API_SERVICE}/v2/station/${station.id}/register`, {
    ...station,
    installed: undefined
  });
}

async function getConfig(stationId: string) {
  const response = await API.post<StationConfigAPIResponse>(`${DATA_API_SERVICE}/v2/station/${stationId}/config`);
  return StationConfig.fromAPI(response.data);
}

async function getUser(id: string) {
  return API.get(`${DATA_API_SERVICE}/connectors/${id}/user`);
}

async function getTopDisconnects(
  query?: Partial<{
    start: Date;
    end: Date;
    offset: number;
    limit: number;
  }>
) {
  const response = await API.post(`${DATA_API_SERVICE}/disconnects/top`, query);
  return response.data;
}

async function getLastPingReceived(id: string) {
  const stationResponse = await API.get(`${DATA_API_SERVICE}/v2/station/${id}/info`);
  return stationResponse.data.lastCommunication;
}

async function getGroupDisconnects(
  query?: Partial<{
    start: Date;
    end: Date;
  }>
) {
  const response = await API.post(`${DATA_API_SERVICE}/disconnects/outages`, query);
  const organizedResponse: any[] = [];
  for (const property in response.data) {
    for (const outage in response.data[property].outage) {
      organizedResponse.push({
        propertyName: response.data[property].propertyName,
        propertyId: response.data[property].propertyId,
        disconnectTotal: response.data[property].outage[outage].disconnectTotal,
        disconnects: response.data[property].outage[outage].disconnects,
        startTime: new Date(response.data[property].outage[outage].startTime),
        endTime: new Date(response.data[property].outage[outage].endTime)
      });
    }
  }
  return organizedResponse;
}

function getListOfUpdatableConfigKeys() {
  return [
    "ActivationFee",
    "AllowOfflineTxForUnknownId",
    "AuthorizationCacheEnabled",
    "AuthorizeRemoteTxRequests",
    "BSSID",
    "BasicAuthId",
    "BasicAuthPwd",
    "BootNotificationInterval",
    "BootNotificationRetries",
    "CellularApnName",
    "CellularApnPassword",
    "CellularApnPdpType",
    "CellularApnUser",
    "CellularDialNumber",
    "CellularPinCode",
    "CentralSystemURL",
    "ChargePointId",
    "ClockAlignedDataInterval",
    "ColdLoadPickupMaxDelay",
    "ConnectionTimeOut",
    "Connectivity",
    "ConnectorPhaseRotation",
    "CustomDisplayCostAndPrice",
    "DownloadFirmwareInterval",
    "DownloadFirmwareRetries",
    "FinalCost",
    "FTPPassword",
    "FTPUserName",
    "FallbackAmperage",
    "GatewaySerialNumber",
    "HeartBeatInterval",
    "LEDBrightness",
    "LlmChargingPolicy",
    "LlmEnable",
    "LlmGridMaxAmperage",
    "LlmGroupId",
    "LlmNumberOfChargers",
    "LocalAuthListEnabled",
    "LocalAuthorizeOffline",
    "LocalPreAuthorize",
    "MaxAmperageFWSetting",
    "MaxEnergyOnInvalidId",
    "MessageTransportLayer",
    "MeterValueSampleInterval",
    "MeterValuesAlignedData",
    "MeterValuesSampledData",
    "MinimumStatusDuration",
    "OperationMode",
    "ParkingFee",
    "ParkingFeeCharging",
    "PlugAndChargeId",
    "PDUTimeout",
    "PlugAndChargeId",
    "ReservationSupported",
    "ResetRetries",
    "RestartTime",
    "ResumeCharging",
    "SSID",
    "SkipHeartBeat",
    "StopTransactionOnEVSideDisconnect",
    "StopTransactionOnInvalidId",
    "StopTxnAlignedData",
    "StopTxnSampledData",
    "TemperatureHigh",
    "TemperatureLow",
    "TransactionMessageAttempts",
    "TransactionMessageRetryInterval",
    "UnitPrice",
    "UnlockConnectorOnEVSideDisconnect",
    "UploadDiagnosticsInterval",
    "UploadDiagnosticsRetries",
    "UseExternalGateway",
    "VentilationArea",
    "VoltageHigh",
    "VoltageLow",
    "WebSocketPingInterval",
    "WiFiPassword",
    "WiFiSecurity",
    "rfidAvailability"
  ];
}

async function updateConfigForStation(stationId: string, key: string, value: string) {
  return remoteStationControl({
    entityId: stationId,
    entityEnum: "STATION",
    action: "UPDATE_CONFIG",
    key,
    value
  });
}

async function updateConfigForProperty(propertyId: string, key: string, value: string) {
  return remoteStationControl({
    entityId: propertyId,
    entityEnum: "PROPERTY",
    action: "UPDATE_CONFIG",
    key,
    value
  });
}

async function updateConfigForOrganization(organizationId: string, key: string, value: string) {
  return remoteStationControl({
    entityId: organizationId,
    entityEnum: "ORGANIZATION",
    action: "UPDATE_CONFIG",
    key,
    value
  });
}

async function softReset(stationId: string) {
  return remoteStationControl({
    entityId: stationId,
    entityEnum: "STATION",
    action: "RESET_SOFT"
  });
}

async function hardReset(stationId: string) {
  return remoteStationControl({
    entityId: stationId,
    entityEnum: "STATION",
    action: "RESET_HARD"
  });
}

async function updateFirmware(stationId: string, firmwareVersion: string) {
  return remoteStationControl({
    entityId: stationId,
    entityEnum: "STATION",
    action: "FIRMWARE_UPDATE",
    key: "VERSION",
    value: firmwareVersion
  });
}

async function getAllUptime(start: Date, end: Date): Promise<EntityUptimeAPIResponse[]> {
  try {
    const res = await apolloClient.query({
      query: gql`
        query StationUptime($dateFilter: DateFilter) {
          metrics(dateFilter: $dateFilter) {
            uuid: station
            pingValue: measureValue
          }
        }
      `,
      variables: {
        dateFilter: {
          startDate: start,
          endDate: end
        }
      }
    });

    return res.data?.metrics || [];
  } catch (e) {
    return [];
  }
}

const APIStation = {
  createStation,
  deleteStation,
  getStationById,
  getStationBySerial,
  getStationsByPropertyId,
  findAll,
  findDisconnected,
  findDisconnectedWithMappedOrg,
  getConfig,
  getUser,
  getDisconnectedHistory,
  getOCPPLogs,
  getListOfUpdatableConfigKeys,
  getUnregisteredStations,
  getTopDisconnects,
  getGroupDisconnects,
  getLastPingReceived,
  hardReset,
  softReset,
  mapStationsToDisconnectStatus,
  registerStation,
  startStation,
  stopStation,
  updateConfigForStation,
  updateConfigForOrganization,
  updateConfigForProperty,
  updateStation,
  updateFirmware,
  getStationFirmware,
  getAllUptime
};

export default APIStation;
